import type { CancelablePromise } from "./core/CancelablePromise"
import { OpenAPI } from "./core/OpenAPI"
import { request as __request } from "./core/request"

import type {
  Message,
  CitiesPublic,
  FormsContactCreate,
  FormsNewsletterCreate,
  FormsNewsletterUnsubscribe,
  FormsNewsletterVerify,
  FormsSubscriptionCreate,
  SubscriptionPublic,
  TagsPublic,
  AuthorPublic,
  PolicyPublic,
  ImprintPublic,
  AGBPublic,
  PublicationPublic,
  PublicPublicationsOrderBy,
  PublicationsPublic,
  LandingpagePublic,
  RateChartsPublic,
  NavigationsPublic,
} from "./models"


export class UtilsService {
  /**
   * Module Versions
   * Get module versions.
   * @returns string Successful Response
   * @throws ApiError
   */
  public static moduleVersions(): CancelablePromise<Record<string, string>> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/utils/module-versions/",
    })
  }
}

export type TDataReadCities = {
  cityName?: string | null
  postcode?: string | null
}

export class CitiesService {
  /**
   * Read Cities
   * Retrieve cities.
   * @returns CitiesPublic Successful Response
   * @throws ApiError
   */
  public static readCities(
    data: TDataReadCities = {},
  ): CancelablePromise<CitiesPublic> {
    const { cityName, postcode } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/cities/",
      query: {
        city_name: cityName,
        postcode,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateFormsContact = {
  recaptchaToken: string
  requestBody: FormsContactCreate
}

export class FormsContactService {
  /**
   * Create Forms Contact
   * Create new contact form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createFormsContact(
    data: TDataCreateFormsContact,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/contact/",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateFormsNewsletter = {
  recaptchaToken: string
  requestBody: FormsNewsletterCreate
}
export type TDataFormsNewsletterUnsubscribe = {
  recaptchaToken: string
  requestBody: FormsNewsletterUnsubscribe
}
export type TDataFormsNewsletterVerify = {
  requestBody: FormsNewsletterVerify
}

export class FormsNewsletterService {
  /**
   * Create Forms Newsletter
   * Create new newsletter form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createFormsNewsletter(
    data: TDataCreateFormsNewsletter,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/newsletter/subscribe",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

/**
   * Forms Newsletter Unsubscribe
   * Request token to unsubscribe from newsletter
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static formsNewsletterUnsubscribe(
    data: TDataFormsNewsletterUnsubscribe,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/newsletter/unsubscribe",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Forms Newsletter Verify
   * Verify newsletter subscribe/unsubscribe tokens.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static formsNewsletterVerify(
    data: TDataFormsNewsletterVerify,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/newsletter/verify",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataCreateFormsSubscription = {
  recaptchaToken: string
  requestBody: FormsSubscriptionCreate
}

export class FormsSubscriptionService {
  /**
   * Create Forms Subscription
   * Create new subscription form submission.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static createFormsSubscription(
    data: TDataCreateFormsSubscription,
  ): CancelablePromise<Message> {
    const { recaptchaToken, requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/subscription/",
      query: {
        recaptcha_token: recaptchaToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class SubscriptionService {
  /**
   * Read Subscription
   * Retrieve public subscription data.
   * @returns SubscriptionPublic Successful Response
   * @throws ApiError
   */
  public static readSubscription(): CancelablePromise<SubscriptionPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/subscription/",
    })
  }
}

export type TDataReadTagsPub = {
  skip?: number
}

export class TagsService {
  /**
   * Read Tags
   * Retrieve tags.
   * @returns TagsPublic Successful Response
   * @throws ApiError
   */
  public static readTagsPub(
    data: TDataReadTagsPub = {},
  ): CancelablePromise<TagsPublic> {
    const { skip = 0 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/tags/",
      query: {
        skip,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export type TDataReadAuthorPublic = {
  authorId: string
}
export class AuthorsService {
  /**
   * Read Author
   * Retrieve public author.
   * @returns AuthorPublic Successful Response
   * @throws ApiError
   */
  public static readAuthorPublic(
    data: TDataReadAuthorPublic,
  ): CancelablePromise<AuthorPublic> {
    const { authorId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/author/{author_id}",
      path: {
        author_id: authorId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }
}

export class PolicyService {
  /**
   * Read Policy
   * Retrieve public policy data.
   * @returns PolicyPublic Successful Response
   * @throws ApiError
   */
  public static readPolicy(): CancelablePromise<PolicyPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/policy/",
    })
  }
}

export class ImprintService {
  /**
   * Read Imprint
   * Retrieve public imprint data.
   * @returns ImprintPublic Successful Response
   * @throws ApiError
   */
  public static readImprint(): CancelablePromise<ImprintPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/imprint/",
    })
  }
}

export class AgbService {
  /**
   * Read Agb
   * Retrieve public AGB data.
   * @returns AGBPublic Successful Response
   * @throws ApiError
   */
  public static readAgb(): CancelablePromise<AGBPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/agb/",
    })
  }
}

export type TDataReadPublicationsPublic = {
  authorId?: string | null
  categoryId?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  descending?: boolean
  orderBy?: PublicPublicationsOrderBy
  query?: string | null
  skip?: number
  tagId?: string | null
}
export type TDataReadPublicationPublic = {
  classicId: number
}
export class PublicationsService {
   /**
   * Read Publications
   * Retrieve public publications.
   * No params validation, query will just not match -> 204 response.
   * @returns PublicationsPublic Successful Response
   * @throws ApiError
   */
  public static readPublicationsPublic(
    data: TDataReadPublicationsPublic = {},
  ): CancelablePromise<PublicationsPublic> {
    const {
      authorId,
      categoryId,
      dateFrom,
      dateTo,
      descending = true,
      orderBy = "publication_dt",
      query,
      skip = 0,
      tagId,
    } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/publications/",
      query: {
        skip,
        order_by: orderBy,
        descending,
        category_id: categoryId,
        author_id: authorId,
        tag_id: tagId,
        date_from: dateFrom,
        date_to: dateTo,
        query,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

  /**
   * Read Publication
   * Retrieve public publication.
   * @returns PublicationPublic Successful Response
   * @throws ApiError
   */
  public static readPublicationPublic(
    data: TDataReadPublicationPublic,
  ): CancelablePromise<PublicationPublic> {
    const { classicId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/publications/{classic_id}",
      path: {
        classic_id: classicId,
      },
      errors: {
        422: `Validation Error`,
      },
    })
  }

}
export class LandingpageService {
  /**
   * Read Landingpage
   * Retrieve publications for landingpage.
   * @returns LandingpagePublic Successful Response
   * @throws ApiError
   */
  public static readLandingpage(): CancelablePromise<LandingpagePublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/landingpage/",
    })
  }
}

export class RatesService {
  /**
   * Read Rates
   * Retrieve public rates data.
   * @returns RateChartsPublic Successful Response
   * @throws ApiError
   */
  public static readRates(): CancelablePromise<RateChartsPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/rates/",
    })
  }
}

export class NavigationService {
  /**
   * Read Navigation
   * Retrieve public navigation settings.
   * @returns NavigationsPublic Successful Response
   * @throws ApiError
   */
  public static readNavigation(): CancelablePromise<NavigationsPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/navigation/",
    })
  }
}